import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Pagination } from 'transfix-ui/components/Pagination'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Table, TableHead, TableBody, TableRow, TableCell } from 'transfix-ui/components/Table'
import { IRawEdiFile, IRawEdiTransaction } from 'apps/edi-tools/types'
import { CreatedAtTableCell, ShipmentTableCell, PartnerTableCells } from './TableCells'

const EdiFilesTableHeader = () => {
  return (
    <TableHead>
      <TableRow>
        <TableCell>Created</TableCell>
        <TableCell>Edi Message Type</TableCell>
        <TableCell>Subtype</TableCell>
        <TableCell>Partner Type</TableCell>
        <TableCell>Partner Id</TableCell>
        <TableCell>Shipment</TableCell>
        <TableCell>EDI File</TableCell>
      </TableRow>
    </TableHead>
  )
}

interface IPropsInboundEdiTransactionRow {
  ediTransaction: IRawEdiTransaction
  ediFile: IRawEdiFile
}

const EdiTransactionTableRow = ({ ediFile, ediTransaction }: IPropsInboundEdiTransactionRow) => {
  const { pathname = '' } = useLocation()
  let pathUri = pathname
  if (pathname[pathname.length - 1] !== '/') pathUri = `${pathname}/`
  const { createdAt, ediMessageType, id, partnerApplicationId, partnerApplicationType } =
    ediFile || {}

  return (
    <TableRow key={id}>
      <CreatedAtTableCell createdAt={createdAt} />
      <TableCell>{`${ediMessageType} inbound`}</TableCell>
      <TableCell className='ttc'>{ediTransaction.subtype?.split('_')?.join(' ') || ''}</TableCell>
      <PartnerTableCells
        partnerApplicationType={partnerApplicationType}
        partnerApplicationId={partnerApplicationId}
      />
      <ShipmentTableCell shipmentId={ediTransaction.shipmentId} />
      <TableCell>
        <Link to={`${pathUri}inbound/${id}/details`}>Details</Link>
      </TableCell>
    </TableRow>
  )
}

interface IPropsEdiFileRow {
  ediFile: IRawEdiFile
}

const EdiFileTableRow = ({ ediFile }: IPropsEdiFileRow) => {
  const { pathname = '' } = useLocation()
  let pathUri = pathname
  if (pathname[pathname.length - 1] !== '/') pathUri = `${pathname}/`
  const {
    createdAt,
    ediMessageType,
    ediMetadata,
    id,
    objectType,
    partnerApplicationId,
    partnerApplicationType,
    shipmentId,
  } = ediFile || {}
  const ediMessageDirection = objectType === 'InboundRawEdiFile' ? 'inbound' : 'outbound'

  return (
    <TableRow key={id}>
      <CreatedAtTableCell createdAt={createdAt} />
      <TableCell>{`${ediMessageType} ${ediMessageDirection}`}</TableCell>
      <TableCell className='ttc'>{ediMetadata?.subtype?.split('_').join(' ') || ''}</TableCell>
      <PartnerTableCells
        partnerApplicationType={partnerApplicationType}
        partnerApplicationId={partnerApplicationId}
      />
      <ShipmentTableCell shipmentId={shipmentId} />
      <TableCell>
        <Link to={`${pathUri}${ediMessageDirection}/${id}/details`}>Details</Link>
      </TableCell>
    </TableRow>
  )
}
interface IProps {
  ediFiles: IRawEdiFile[]
  currentPage: number
  numberOfPages: number
  onPageChange: (pageNumber: number) => void
  loading: boolean
  loadingError: boolean
}

export const RawEdiFilesTable = ({
  currentPage,
  ediFiles,
  loading,
  loadingError,
  numberOfPages,
  onPageChange,
}: IProps) => (
  <div>
    {!loading && !ediFiles.length && (
      <InlineMessage
        message='There are no EDI files matching the criteria.'
        type={INLINE_MESSAGE_VARIANTS.INFO}
      />
    )}
    {!loading && loadingError && (
      <InlineMessage
        message='There was an error loading EDI files.'
        type={INLINE_MESSAGE_VARIANTS.INFO}
      />
    )}
    <Table>
      <EdiFilesTableHeader />
      <TableBody>
        {loading ? (
          <Spinner position='relative' />
        ) : (
          ediFiles
            .map(ediFile => {
              const { transactions = [], id } = ediFile || {}

              if (transactions.length) {
                return transactions.map(ediTransaction => (
                  <EdiTransactionTableRow
                    key={id}
                    ediFile={ediFile}
                    ediTransaction={ediTransaction}
                  />
                ))
              }

              return <EdiFileTableRow key={id} ediFile={ediFile} />
            })
            .flat()
        )}
      </TableBody>
    </Table>
    {!loading && numberOfPages > 1 && (
      <Pagination
        currentPage={currentPage}
        numberOfPages={numberOfPages}
        onPageChange={onPageChange}
      />
    )}
  </div>
)
